import { createContext, Dispatch, useContext, useReducer } from "react";
import { ActionMap } from "../ActionMap";
import { ResourceDuplicateModal } from "./ResourceDuplicateModal";

type InitialStateType = Partial<DuplicateResourcePayload>;

const initialState = { resource: undefined, duplicateAction: undefined, onSuccess: undefined };

export const DuplicateResourceContext = createContext<{
  state: InitialStateType;
  dispatch: Dispatch<Actions>;
}>({
  state: initialState,
  dispatch: () => null,
});

export enum DuplicateResourceActionTypes {
  SetDuplicateResource = "SET_DUPLICATE_RESOURCE",
  ClearDuplicateResource = "CLEAR_DUPLICATE_RESOURCE",
}

export const DuplicateResourceContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = useReducer(duplicateResourceReducer, initialState);

  return (
    <DuplicateResourceContext.Provider value={{ state, dispatch }}>
      <>
        {children}
        <ResourceDuplicateModal />
      </>
    </DuplicateResourceContext.Provider>
  );
};

export const useDuplicateResourceActions = () => {
  const { dispatch } = useContext(DuplicateResourceContext);

  return {
    setDuplicateResource: (duplicateResource: DuplicateResourcePayload) =>
      deleteResourceActions.setDeleteResource(dispatch, duplicateResource),
    clearDuplicateResource: () => deleteResourceActions.clearDeleteResource(dispatch),
  };
};

export const useDuplicateResourceState = () => {
  const { state } = useContext(DuplicateResourceContext);

  return state;
};

type DuplicateResourcePayload = {
  resource: { id: number; name: string; description?: string };
  duplicateAction: (id: number) => Promise<number>;
  onSuccess?: (newId: number) => void;
};

type Payload = {
  [DuplicateResourceActionTypes.SetDuplicateResource]: DuplicateResourcePayload;
  [DuplicateResourceActionTypes.ClearDuplicateResource]: undefined;
};

type Actions = ActionMap<Payload>[keyof ActionMap<Payload>];

export const duplicateResourceReducer = (state: InitialStateType, action: Actions): InitialStateType => {
  switch (action.type) {
    case DuplicateResourceActionTypes.SetDuplicateResource:
      return {
        ...state,
        resource: action.payload.resource,
        duplicateAction: action.payload.duplicateAction,
        onSuccess: action.payload.onSuccess,
      };
    case DuplicateResourceActionTypes.ClearDuplicateResource:
      return initialState;

    default:
      return state;
  }
};

export const deleteResourceActions = {
  setDeleteResource: (dispatch: Dispatch<Actions>, duplicateResource: DuplicateResourcePayload) =>
    dispatch({
      type: DuplicateResourceActionTypes.SetDuplicateResource,
      payload: duplicateResource,
    }),
  clearDeleteResource: (dispatch: Dispatch<Actions>) =>
    dispatch({ type: DuplicateResourceActionTypes.ClearDuplicateResource }),
};
